import React, { useState, useContext, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { useLocation } from "react-router-dom";
import Select from "react-select";

// 은행 목록 데이터
const bankOptions = [
  { value: "KB", label: "KB국민은행", icon: "/bank-icons/kb.png" },
  { value: "shinhan", label: "신한은행", icon: "/bank-icons/shinhan.png" },
  { value: "woori", label: "우리은행", icon: "/bank-icons/woori.png" },
  { value: "smk", label: "새마을금고", icon: "/bank-icons/smk.png" },
  { value: "hana", label: "하나은행", icon: "/bank-icons/hana.png" },
  { value: "nh", label: "농협은행", icon: "/bank-icons/nh.png" },
  { value: "sh", label: "신협은행", icon: "/bank-icons/sh.png" },
  { value: "ibk", label: "기업은행", icon: "/bank-icons/ibk.png" },
  { value: "sc", label: "SC제일은행", icon: "/bank-icons/sc.png" },
  { value: "kakao", label: "카카오뱅크", icon: "/bank-icons/kakao.png" },
  { value: "toss", label: "토스뱅크", icon: "/bank-icons/toss.png" },
  { value: "kdb", label: "산업은행", icon: "/bank-icons/kdb.png" },
  { value: "city", label: "한국씨티은행", icon: "/bank-icons/city.png" },
  { value: "dgb", label: "대구은행", icon: "/bank-icons/dgb.png" },
  { value: "bnk", label: "부산은행", icon: "/bank-icons/bnk.png" },
  { value: "kjb", label: "광주은행", icon: "/bank-icons/kjb.png" },
  { value: "knb", label: "경남은행", icon: "/bank-icons/bnk.png" },
  { value: "kbank", label: "케이뱅크", icon: "/bank-icons/kbank.png" },
  { value: "suhyup", label: "Sh수협은행", icon: "/bank-icons/suhyup.png" },
  { value: "jeju", label: "제주은행", icon: "/bank-icons/jeju.png" },
  { value: "post", label: "우체국", icon: "/bank-icons/post.png" },
];

// 진동 애니메이션
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

// 폼 섹션 애니메이션 variants 추가
const formVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const formContainerVariants = {
  initial: {
    height: "auto",
    opacity: 0,
  },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 },
    },
  },
  exit: {
    height: "auto",
    opacity: 0,
  },
};

const formContentVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  },
  exit: (direction) => ({
    x: direction < 0 ? 500 : -500,
    opacity: 0,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  }),
};

// 커스텀 옵션 컴포넌트
const CustomOption = ({ innerProps, label, data }) => (
  <OptionContainer {...innerProps}>
    <BankIcon src={data.icon} alt={label} />
    <span>{label}</span>
  </OptionContainer>
);

// 커스텀 단일 값 컴포넌트
const CustomSingleValue = ({ children, data }) => (
  <SingleValueContainer>
    <BankIcon src={data.icon} alt={children} />
    <span>{children}</span>
  </SingleValueContainer>
);

// 토스트 애니메이션
const toastVariants = {
  initial: {
    opacity: 0,
    y: -50, // 상단에서 아래로 나타나는 효과
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    y: -50, // 다시 상단으로 사라지는 효과
    transition: {
      duration: 0.3,
    },
  },
};

const UserDataCollection = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState(null);
  const [[step, direction], setStep] = useState([1, 0]); // direction 추가

  const [form, setForm] = useState({
    name: "",
    birth: "",
    email: "",
    address: "",
    postcode: "",
    detailAddress: "",
    bank: null,
    accountNumber: "",
    accountHolder: "",
    phoneNumber: "",
    telecom: "",
    referralSourceCode: "",
    referralDetailCode: "",
  });

  // 동의 상태 추가
  const [consents, setConsents] = useState({
    victimConfirmation: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [errors, setErrors] = useState({
    bank: "",
    accountNumber: "",
    accountHolder: "",
    victimConfirmation: "",
  });

  const [shakingFields, setShakingFields] = useState({
    bank: false,
    accountNumber: false,
    accountHolder: false,
    victimConfirmation: false,
  });

  // 토스트 알림을 위한 상태 추가
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 확인 모달 상태 추가
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // 토스트 메시지 표시 함수
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const addressRef = useRef(null);
  const detailAddressRef = useRef(null);

  // 카카오 주소 API 스크립트 로드
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // URL 파라미터에서 redirect 값 추출
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get("redirect");
    if (redirect) {
      setRedirectPath(`/${redirect}`);
    }
  }, [location]);

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    forceScrollTop();
  }, []);

  // 단계 변경 시 스크롤 초기화
  useEffect(() => {
    forceScrollTop();
  }, [step]);

  // 페이지 로드 시 신청 유형 확인
  useEffect(() => {
    // 신청 유형 쿠키 확인
    const applicationType = Cookies.get("applicationType");

    // 페이지 타이틀 동적 설정
    if (applicationType === "representative") {
      // 대표자 신청일 경우 헤더 텍스트 변경
      if (document.title) {
        document.title = "대표자 신청 - 기본 정보 입력";
      }
    }
  }, []);

  // 신청 유형에 따라 다른 헤더 텍스트 표시
  const applicationType = Cookies.get("applicationType");
  const isRepresentative = applicationType === "representative";

  // 다음 주소 검색 실행 함수
  const openPostcodeSearch = () => {
    // 기존 container가 있다면 제거
    const existingContainer = document.getElementById("postcode-container");
    if (existingContainer) {
      existingContainer.remove();
    }

    // 새 container 생성
    const postcodeContainer = document.createElement("div");
    postcodeContainer.id = "postcode-container";
    postcodeContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    // 내부 컨테이너 생성
    const innerContainer = document.createElement("div");
    innerContainer.style.cssText = `
      position: relative;
      width: 90%;
      max-width: 500px;
      height: 500px;
      background-color: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    // 내부 컨테이너 클릭 시 이벤트 버블링 방지
    innerContainer.onclick = function (e) {
      e.stopPropagation();
    };

    postcodeContainer.appendChild(innerContainer);
    document.body.appendChild(postcodeContainer);

    // 외부 영역 클릭 시 모달 닫기
    postcodeContainer.onclick = function () {
      document.body.removeChild(postcodeContainer);
    };

    new window.daum.Postcode({
      oncomplete: function (data) {
        // 도로명 주소 또는 지번 주소 선택
        let addr = data.roadAddress || data.jibunAddress;

        // 주소 및 우편번호 필드 업데이트
        setForm((prev) => ({
          ...prev,
          address: addr,
          postcode: data.zonecode || "",
        }));

        // 상세주소 필드에 포커스
        setTimeout(() => {
          if (detailAddressRef.current) {
            detailAddressRef.current.focus();
          }
        }, 100);

        // 검색 완료 후 컨테이너 제거
        document.body.removeChild(postcodeContainer);
      },
      // 검색창 크기 및 UI 설정
      width: "100%",
      height: "100%",
      maxSuggestItems: 5,
      theme: {
        searchBgColor: "#FFFFFF", // 검색창 배경색을 흰색으로 변경
        queryTextColor: "#333333", // 검색창 텍스트 색상을 어두운 색으로 변경
        emphTextColor: "#E31C20", // 강조 텍스트 색상
        outlineColor: "#e0e0e0", // 테두리 색상
      },
      alwaysShowEngAddr: false,
      hideMapBtn: true,
      hideEngBtn: true,
      shorthand: true,
      animation: true,
    }).embed(innerContainer);
  };

  const handleChange = (key) => (e) => {
    let value = e.target.value;

    setForm((f) => ({ ...f, [key]: value }));

    // 입력 중에는 에러 메시지 초기화
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  // 생년월일 유효성 검사
  const validateBirth = (birth) => {
    const birthRegex = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;
    if (!birthRegex.test(birth)) {
      return "올바른 생년월일 형식이 아닙니다. (예: 19990315)";
    }

    // 미성년자 확인 (2007년 1월 1일 이후 출생자)
    if (parseInt(birth) >= 20070101) {
      return "미성년자는 신청이 불가합니다. (2007년 1월 1일 이후 출생자)";
    }

    return "";
  };

  // 이메일 유효성 검사
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(email)) {
      return "올바른 이메일 주소 형식이 아닙니다.";
    }
    return "";
  };

  // 계좌번호 유효성 검사
  const validateAccountNumber = (accountNumber) => {
    // 숫자와 하이픈만 허용
    const accountRegex = /^[0-9-]+$/;
    if (!accountRegex.test(accountNumber)) {
      return "계좌번호는 숫자만 입력 가능합니다.";
    }
    return "";
  };

  // 예금주명 유효성 검사
  const validateAccountHolder = (accountHolder) => {
    if (!accountHolder || accountHolder.trim().length < 2) {
      return "예금주명을 정확히 입력해주세요.";
    }

    // IdentityVerificationInputPage에서 입력한 이름과 비교
    // 우선 pendingVerificationName 확인 (인증수단 없이 넘어온 경우)
    const pendingName = Cookies.get("pendingVerificationName");
    // 없으면 verifiedName 확인 (본인인증 완료한 경우)
    const verifiedName = Cookies.get("verifiedName");

    // IdentityVerificationInputPage에서 입력한 이름 (우선순위: pendingVerificationName > verifiedName)
    const inputName = pendingName || verifiedName;
    if (!inputName) {
      // 이름이 없으면 검증하지 않음
      return "";
    }

    // 공백 제거 후 비교
    const accountHolderTrimmed = accountHolder.trim();
    const inputNameTrimmed = inputName.trim();

    if (accountHolderTrimmed !== inputNameTrimmed) {
      return "예금주명은 입력하신 이름과 동일해야 합니다.";
    }

    return "";
  };

  // 진동 효과 실행 후 초기화
  useEffect(() => {
    // 진동 효과가 있는 필드 확인
    const hasShakingField = Object.values(shakingFields).some((val) => val);

    if (hasShakingField) {
      const timer = setTimeout(() => {
        setShakingFields({
          bank: false,
          accountNumber: false,
          accountHolder: false,
          victimConfirmation: false,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shakingFields]);

  // 모든 가능한 스크롤 위치를 0으로 강제 설정하는 함수
  const forceScrollTop = () => {
    // 메인 문서 요소들의 스크롤 초기화
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // iOS/Safari 대응
    if (window.pageYOffset !== undefined) {
      window.scrollTo(0, 0);
    }

    // 컨텐츠 래퍼 스크롤 초기화
    const contentWrapper = document.querySelector(".content-wrapper");
    if (contentWrapper) {
      contentWrapper.scrollTop = 0;
    }

    // 추가 스크롤 초기화를 위해 지연 실행
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 50);
  };

  // 첫 번째 단계 완료 처리
  const handleFirstStepComplete = async () => {
    if (!isFirstStepValid()) {
      return;
    }

    // 주소 값 검증 및 정리 (빈 문자열을 null로 변환)
    const addressValue = form.address?.trim() || null;
    const addressDetailValue = form.detailAddress?.trim() || null;
    const zipCodeValue = form.postcode?.trim() || null;

    // address는 필수이므로 빈 값이면 에러 발생
    if (!addressValue) {
      setApiError("주소를 입력해주세요.");
      return;
    }

    setIsLoading(true);
    setApiError("");

    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          address: addressValue,
          addressDetail: addressDetailValue,
          zipCode: zipCodeValue,
        }),
      });

      // 응답 처리
      if (!response.ok) {
        throw new Error("주소 업데이트 실패");
      }

      // 주소 정보를 별도 쿠키에 저장 (공동 소유자 페이지에서 사용)
      Cookies.set(
        "representativeAddress",
        JSON.stringify({
          address: form.address.trim(),
          detailAddress: form.detailAddress.trim(),
        }),
        { expires: 7 },
      );

      // 스크롤 위치를 강제로 맨 위로 이동
      forceScrollTop();

      setStep([2, 1]); // 오른쪽으로 이동
    } catch (error) {
      console.error("주소 업데이트 오류:", error);
      setApiError("주소 저장 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 두 번째 단계 완료 처리
  const handleSecondStepComplete = () => {
    if (isSecondStepValid()) {
      // 확인 모달 표시
      setShowConfirmationModal(true);
    }
  };

  // 확인 모달에서 확인 버튼 클릭 시
  const handleConfirmAndSubmit = () => {
    // 검증은 "다음으로" 버튼에서 이미 수행했으므로 바로 제출
    setShowConfirmationModal(false);
    handleSubmit();
  };

  // 이전 단계로 이동하는 함수 추가
  const handlePrevStep = () => {
    // 스크롤 위치를 강제로 맨 위로 이동
    forceScrollTop();

    if (step === 2) {
      setStep([1, -1]); // 첫 번째 단계로
    }
  };

  // 첫 번째 단계 유효성 검사
  const isFirstStepValid = () => {
    // 주소만 필수이므로 항상 true 반환
    return true;
  };

  // 첫 번째 단계 입력 완료 여부 확인
  const isFirstStepFilled = () => {
    return form.address.trim() !== "";
  };

  // 두 번째 단계 입력 완료 여부 확인
  const isSecondStepFilled = () => {
    // 기본 필수 필드 확인
    if (
      form.bank === null ||
      form.accountNumber.trim() === "" ||
      form.accountHolder.trim() === ""
    ) {
      return false;
    }

    // 쿠키에 이름값이 있는지 확인하고 예금주명 검증
    const pendingName = Cookies.get("pendingVerificationName");
    const verifiedName = Cookies.get("verifiedName");
    const inputName = pendingName || verifiedName;

    // 쿠키에 이름값이 있으면 검증 수행
    if (inputName) {
      const accountHolderError = validateAccountHolder(form.accountHolder);
      if (accountHolderError) {
        // 에러가 있으면 버튼 비활성화
        return false;
      }
    }

    // 계좌번호 검증도 확인
    const accountNumberError = validateAccountNumber(form.accountNumber);
    if (accountNumberError) {
      return false;
    }

    return true;
  };

  // 두 번째 단계 유효성 검사
  const isSecondStepValid = () => {
    const accountNumberError = validateAccountNumber(form.accountNumber);
    const accountHolderError = validateAccountHolder(form.accountHolder);
    let isValid = true;

    setErrors({
      bank: !form.bank ? "은행을 선택해주세요." : "",
      accountNumber: accountNumberError,
      accountHolder: accountHolderError,
    });

    if (!form.bank) {
      setShakingFields({ ...shakingFields, bank: true });
      isValid = false;
    } else if (accountNumberError) {
      accountNumberRef.current.focus();
      setShakingFields({ ...shakingFields, accountNumber: true });
      isValid = false;
    } else if (accountHolderError) {
      accountHolderRef.current.focus();
      setShakingFields({ ...shakingFields, accountHolder: true });
      isValid = false;
    }

    return isValid;
  };

  // 세 번째 단계 입력 완료 여부 확인
  const isThirdStepFilled = () => {
    return consents.victimConfirmation;
  };

  // 동의 체크박스 변경 핸들러
  const handleConsentChange = (consentType) => {
    setConsents((prev) => ({
      ...prev,
      [consentType]: !prev[consentType],
    }));

    // 에러 메시지 제거
    if (errors[consentType]) {
      setErrors((prev) => ({
        ...prev,
        [consentType]: "",
      }));
    }
  };

  // 세 번째 단계 유효성 검사
  const validateConsents = () => {
    const newErrors = {};
    const newShakingFields = {};

    // 필수 동의 항목 검사
    if (!consents.victimConfirmation) {
      newErrors.victimConfirmation = "유의사항에 동의해주세요.";
      newShakingFields.victimConfirmation = true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    setShakingFields((prev) => ({ ...prev, ...newShakingFields }));

    // 흔들림 효과 제거
    setTimeout(() => {
      setShakingFields((prev) => ({
        ...prev,
        victimConfirmation: false,
      }));
    }, 500);

    return Object.keys(newErrors).length === 0;
  };

  // 최종 제출 처리
  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      // 계좌 정보를 쿠키에 저장 (NewsProofSubmission.js에서 사용)
      Cookies.set("bankCode", form.bank ? form.bank.label : "", { expires: 7 });
      Cookies.set("accountNumber", form.accountNumber.trim(), { expires: 7 });
      Cookies.set("accountHolder", form.accountHolder.trim(), { expires: 7 });

      // 동의 정보를 sessionStorage에 저장
      sessionStorage.setItem("consentData", JSON.stringify(consents));

      // 사용자 정보 쿠키에 저장
      Cookies.set("userData", JSON.stringify(form), { expires: 7 });

      // 신청 유형에 따라 다른 페이지로 리디렉션
      const applicationType = Cookies.get("applicationType") || "self";
      if (applicationType === "self") {
        // 본인 신청의 경우 스크린샷 페이지로 이동
        navigateTo(redirectPath || "/screenshot");
      } else {
        // 대표자 신청의 경우 공동 소유자 페이지로 이동
        navigateTo(redirectPath || "/co-owner-data");
      }
    } catch (error) {
      setApiError("데이터 저장 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 은행 선택 핸들러
  const handleBankChange = (selectedOption) => {
    setForm((prev) => ({ ...prev, bank: selectedOption }));
    if (errors.bank) {
      setErrors((prev) => ({ ...prev, bank: "" }));
    }
  };

  // 계좌번호 입력 핸들러
  const accountNumberRef = useRef(null);
  const accountHolderRef = useRef(null);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 영어 문자 확인 함수
  const containsEnglish = (text) => {
    return /[a-zA-Z]/.test(text);
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          {/* 추가: 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ToastIcon>!</ToastIcon>
                {toastMessage}
              </Toast>
            )}
          </AnimatePresence>

          <Header>
            <SubTitle>
              {step === 1
                ? isRepresentative
                  ? "대표자 정보 입력"
                  : "신원정보 입력"
                : "계좌정보 입력"}
            </SubTitle>
            <Description>
              {step === 1
                ? isRepresentative
                  ? "소송 참여를 위한 대표자 정보를 입력해주세요"
                  : "소송 서류 작성에 필요한 정보를 입력해주세요"
                : "보상금 수령을 위한 계좌 정보를 입력해주세요"}
            </Description>
          </Header>

          {/* 안내문구 */}
          {step === 1 && (
            <InfoBox>
              <InfoIcon>ⓘ</InfoIcon>
              <InfoText>주민등록초본 상 현주소지를 입력해야합니다.</InfoText>
            </InfoBox>
          )}

          <AnimatePresence mode="wait">
            <FormContainer
              as={motion.div}
              key={step}
              variants={formContainerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {apiError && <ErrorAlert>{apiError}</ErrorAlert>}
              <AnimatePresence mode="wait" custom={direction}>
                <FormSection
                  as={motion.div}
                  key={step}
                  custom={direction}
                  variants={formContentVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  {step === 1 ? (
                    // 첫 번째 단계 - 주소 정보 입력
                    <>
                      {/* 주소 입력 섹션 */}
                      <AddressSection>
                        <SectionTitle>주소를 입력해주세요</SectionTitle>
                        <FormGroup>
                          <Label>
                            주소<Required>*</Required>
                          </Label>
                          <AddressInputContainer>
                            <AddressInput
                              type="text"
                              placeholder="주소 검색"
                              value={form.address}
                              readOnly
                              ref={addressRef}
                              onClick={openPostcodeSearch}
                            />
                          </AddressInputContainer>
                        </FormGroup>

                        <FormGroup>
                          <Label>우편번호</Label>
                          <Input
                            type="text"
                            placeholder="우편번호"
                            value={form.postcode}
                            readOnly
                            style={{
                              backgroundColor: "#f5f5f5",
                              cursor: "not-allowed",
                            }}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label>상세주소</Label>
                          <Input
                            type="text"
                            placeholder="상세 주소를 입력해주세요"
                            value={form.detailAddress}
                            onChange={handleChange("detailAddress")}
                            ref={detailAddressRef}
                          />
                        </FormGroup>
                      </AddressSection>
                    </>
                  ) : step === 2 ? (
                    // 두 번째 단계 - 은행 정보 입력
                    <>
                      {/* 안내문구 */}
                      <InfoBox>
                        <InfoIcon>ⓘ</InfoIcon>
                        <InfoText>
                          보상금 수령을 위해 본인 명의 계좌만 가능합니다.
                        </InfoText>
                      </InfoBox>

                      {/* 계좌정보 입력 섹션 */}
                      <AccountSection>
                        <SectionTitle>계좌정보를 입력해주세요</SectionTitle>
                        <InputContainer $isShaking={shakingFields.bank}>
                          <Label>
                            은행 선택<Required>*</Required>
                          </Label>
                          <StyledSelect
                            options={bankOptions}
                            value={form.bank}
                            onChange={handleBankChange}
                            placeholder="은행을 선택해주세요"
                            isSearchable={true}
                            classNamePrefix="select"
                            noOptionsMessage={() => "검색 결과가 없습니다"}
                            loadingMessage={() => "검색 중..."}
                            menuPortalTarget={document.body}
                            components={{
                              Option: CustomOption,
                              SingleValue: CustomSingleValue,
                            }}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                            }}
                          />
                          {errors.bank && <ErrorText>{errors.bank}</ErrorText>}
                        </InputContainer>

                        <InputContainer
                          $isShaking={shakingFields.accountNumber}
                        >
                          <Label>
                            계좌번호<Required>*</Required>
                          </Label>
                          <Input
                            ref={accountNumberRef}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={form.accountNumber}
                            onChange={handleChange("accountNumber")}
                            placeholder="계좌번호를 입력해주세요"
                            isNumeric
                          />
                          {errors.accountNumber && (
                            <ErrorText>{errors.accountNumber}</ErrorText>
                          )}
                        </InputContainer>

                        <InputContainer
                          $isShaking={shakingFields.accountHolder}
                        >
                          <Label>
                            예금주명<Required>*</Required>
                          </Label>
                          <Input
                            ref={accountHolderRef}
                            type="text"
                            value={form.accountHolder}
                            onChange={(e) => {
                              setForm((f) => ({
                                ...f,
                                accountHolder: e.target.value,
                              }));
                            }}
                            onBlur={(e) => {
                              // 값이 비어있지 않은 경우에만 검증 및 필터링 적용
                              if (e.target.value.trim()) {
                                const filteredName = e.target.value.trim();

                                // 예금주명 검증 수행
                                const accountHolderError =
                                  validateAccountHolder(filteredName);

                                // 에러 설정
                                if (accountHolderError) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    accountHolder: accountHolderError,
                                  }));
                                  setShakingFields((prev) => ({
                                    ...prev,
                                    accountHolder: true,
                                  }));
                                } else {
                                  // 에러가 없으면 에러 메시지 제거
                                  setErrors((prev) => ({
                                    ...prev,
                                    accountHolder: "",
                                  }));
                                }

                                // 폼 값 업데이트 (공백 제거된 값)
                                setForm((f) => ({
                                  ...f,
                                  accountHolder: filteredName,
                                }));
                              }
                            }}
                            placeholder="예금주명을 입력해주세요"
                          />
                          {errors.accountHolder && (
                            <ErrorText>{errors.accountHolder}</ErrorText>
                          )}
                        </InputContainer>
                      </AccountSection>
                    </>
                  ) : null}
                </FormSection>
              </AnimatePresence>
            </FormContainer>
          </AnimatePresence>

          <ButtonsContainer>
            {step === 2 && (
              <PrevButton
                onClick={handlePrevStep}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                이전으로
              </PrevButton>
            )}
            <ActionButton
              onClick={
                step === 1 ? handleFirstStepComplete : handleSecondStepComplete
              }
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                (step === 1 && !isFirstStepFilled()) ||
                (step === 2 && !isSecondStepFilled()) ||
                isLoading
              }
            >
              {isLoading ? "처리 중..." : "다음으로"}
            </ActionButton>
          </ButtonsContainer>

          {/* 확인 모달 */}
          <AnimatePresence>
            {showConfirmationModal && (
              <ModalOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowConfirmationModal(false)}
              >
                <ModalContent
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CloseButton onClick={() => setShowConfirmationModal(false)}>
                    ×
                  </CloseButton>
                  <ModalTitle>입력한 정보를 확인해주세요</ModalTitle>

                  <InfoSection>
                    <InfoLabel>주소</InfoLabel>
                    <InfoValue>{form.address || "-"}</InfoValue>
                  </InfoSection>

                  <InfoDivider />

                  <InfoSection>
                    <InfoLabel>우편번호</InfoLabel>
                    <InfoValue>{form.postcode || "-"}</InfoValue>
                  </InfoSection>

                  <InfoDivider />

                  {form.detailAddress && (
                    <>
                      <InfoSection>
                        <InfoLabel>상세주소</InfoLabel>
                        <InfoValue>{form.detailAddress}</InfoValue>
                      </InfoSection>
                      <InfoDivider />
                    </>
                  )}

                  <InfoSection>
                    <InfoLabel>은행</InfoLabel>
                    <InfoValue>{form.bank ? form.bank.label : "-"}</InfoValue>
                  </InfoSection>

                  <InfoDivider />

                  <InfoSection>
                    <InfoLabel>계좌번호</InfoLabel>
                    <InfoValue>{form.accountNumber || "-"}</InfoValue>
                  </InfoSection>

                  <InfoDivider />

                  <InfoSection>
                    <InfoLabel>예금주</InfoLabel>
                    <InfoValue>{form.accountHolder || "-"}</InfoValue>
                  </InfoSection>

                  <ConfirmButton
                    onClick={handleConfirmAndSubmit}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    확인했습니다
                  </ConfirmButton>
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 추가 스타일
const AddressInputContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const AddressInput = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  cursor: pointer;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #e31c20;
    outline: none;
  }
`;

const SearchButton = styled.button`
  min-width: 60px;
  height: 50px;
  border-radius: 8px;
  background-color: #e31c20;
  color: white;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #b01418;
  }
`;

// 애니메이션 정의
const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const InfoBox = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-sizing: border-box;
`;

const InfoIcon = styled.span`
  color: #e31c20;
  margin-top: 0px;
  font-size: 16px;
  margin-right: 0px;
  font-weight: bold;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  margin: 0;
  flex: 1;
`;

const AddressSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
`;

const AccountSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  align-self: flex-start;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`;

const InfoTextBold = styled.span`
  color: #4b5563;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;

const HighlightedText = styled.span`
  color: #dc4b3f;
  font-weight: 800;
`;

const InfoTextBold2 = styled.span`
  color: #4b5563;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin-top: 5px;
`;

const InfoTextRegular = styled.span`
  color: #4b5563;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 50px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #e31c20;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 5px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family:
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    "Open Sans",
    "Helvetica Neue",
    sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const FormContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  will-change: height;
`;

const FormSection = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px 0;
`;

const FormGroup = styled.div`
  width: 100%;
  margin-bottom: 0px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2e3840;
`;

const Required = styled.span`
  color: #fe7156;
  margin-left: 2px;
`;

const Input = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? "#FF4D4F" : "#e5e5e5")};
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  animation: ${(props) => (props.isShaking ? shakeAnimation : "none")} 0.5s;
  letter-spacing: ${(props) => (props.isNumeric ? "2px" : "normal")};

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }

  &:focus {
    border-color: ${(props) => (props.hasError ? "#FF4D4F" : "#E31C20")};
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
`;

const ButtonsContainer = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #e31c20;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

// 추가 스타일
const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

// 새로운 스타일 컴포넌트 추가
const StyledSelect = styled(Select)`
  .select__control {
    height: 50px;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: none;
    background: white;

    &:hover {
      border-color: #e31c20;
    }

    &--is-focused {
      border-color: #e31c20;
      box-shadow: 0 0 0 1px #e31c20;
    }
  }

  .select__placeholder {
    color: #999;
  }

  .select__single-value {
    color: #333;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .select__input {
    color: #333 !important;

    input {
      color: #333 !important;
    }
  }

  .select__input-container {
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #333;

    input {
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      border: none !important;
      position: absolute;
      color: #333 !important;

      &:focus {
        opacity: 1 !important;
        width: 100% !important;
        height: auto !important;
        position: relative;
      }
    }
  }

  .select__value-container {
    padding: 2px 16px;
    cursor: pointer;
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .select__menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 8px;
    z-index: 100;
    position: absolute;
    width: 100%;
    background: white;
  }

  .select__menu-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 8px 0;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;

      &:hover {
        background: #555;
      }
    }
  }

  .select__indicator-separator {
    display: none;
  }

  .select__clear-indicator {
    cursor: pointer;
    color: #999;

    &:hover {
      color: #666;
    }
  }

  .select__dropdown-indicator {
    color: #999;
    padding: 0 16px;

    &:hover {
      color: #666;
    }
  }
`;

// 스타일 컴포넌트 추가
const InputContainer = styled.div`
  margin-bottom: 0px;
  width: 100%;
  animation: ${(props) => (props.$isShaking ? shake : "none")} 0.5s;
`;

const ErrorText = styled.p`
  color: #ff4c4c;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
`;

// 이전 버튼 스타일 추가
const PrevButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #e31c20;
  background: #ffffff;
  color: #e31c20;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #efefef;
  }
`;

// 새로운 스타일 컴포넌트 추가
const OptionContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  min-height: 40px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const SingleValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  position: relative;
  top: 0;
`;

const BankIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const InfoTextHelper = styled.p`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

// 새로운 스타일 컴포넌트 추가
const Toast = styled(motion.div)`
  position: fixed;
  top: 230px;
  left: 5%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    max-width: 400px;
    min-width: 40%;
    left: 28.5%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 90%;
  max-width: 90%;
  text-align: left;
`;

const ToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #e31c20;
  color: white;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
`;

// 동의 관련 스타일 컴포넌트 추가
const ConsentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConsentNotice = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
`;

const NoticeTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const NoticeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
`;

const NoticeItem = styled.li`
  font-size: 13px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const NoticeHighlight = styled.span`
  color: #fe7156;
  font-weight: 600;
`;

const ConsentCheckbox = styled.div`
  margin-top: 16px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  animation: ${(props) => (props.isShaking ? shake : "none")} 0.5s;

  &:hover {
    background-color: rgba(0, 117, 255, 0.05);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #e31c20;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
  line-height: 1.5;
  flex: 1;
`;

// 확인 모달 스타일
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  padding: 24px;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin: 0 0 24px 0;
  text-align: left;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 600;
  word-break: break-word;
`;

const InfoDivider = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  width: 100%;
`;

const ConfirmButton = styled(motion.button)`
  width: 100%;
  height: 56px;
  background-color: #e31c20;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 24px;

  &:hover {
    background-color: #b01418;
  }

  &:active {
    background-color: #e31c20;
  }
`;

export default UserDataCollection;
